import { Add, Delete } from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import { formatMoney, formatMoneyBack } from 'lib/functions/utils';
import { RequestExpenseCategory } from 'lib/types/models';
import React, { useEffect, useState } from 'react';

interface RequestExpensesProps {
  requestExpensesCategories: RequestExpenseCategory[];
  expenses: { name: string; amount: number; hasIva: boolean }[];
  setExpenses: (
    expenses: { name: string; amount: number; hasIva: boolean }[],
  ) => void;
  requestConfiguration: {
    expensesHasIVA: boolean;
    factoringOperationsSystemFlag: boolean;
    canCreateRequestExpenses: boolean;
  };
  onEditing: boolean;
}

const RequestExpenses = (props: RequestExpensesProps) => {
  const {
    requestExpensesCategories,
    expenses,
    setExpenses,
    requestConfiguration,
    onEditing,
  } = props;

  const [openPopper, setOpenPopper] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(
    null,
  );

  // Ensure "Otros gastos" is always present
  useEffect(() => {
    const hasDefaultExpense = expenses.some(
      (exp) => exp.name === 'Otros gastos',
    );
    if (!hasDefaultExpense) {
      const defaultExpense = {
        name: 'Otros gastos',
        amount: 0,
        hasIva: requestConfiguration.expensesHasIVA,
      };
      setExpenses([defaultExpense, ...expenses]);
    }
  }, []);

  // Calculate total amount
  const totalAmount = expenses.reduce((sum, exp) => sum + exp.amount, 0);

  const handleTogglePopper = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpenPopper(!openPopper);
  };

  const handleOpenAddMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setOpenMenu(true);
  };

  const handleCloseAddMenu = () => {
    setOpenMenu(false);
    setMenuAnchorEl(null);
  };

  const handleAddCategoryExpense = (category: RequestExpenseCategory) => {
    const newExpense = {
      name: category.name,
      amount: 0,
      hasIva: requestConfiguration.expensesHasIVA ? category.hasIva : false,
    };
    const updatedExpenses = mergeExpenses([...expenses, newExpense]);
    setExpenses(updatedExpenses);
    handleCloseAddMenu();
  };

  const handleAddBlankExpense = () => {
    const newExpense = {
      name: '',
      amount: 0,
      hasIva: requestConfiguration.expensesHasIVA,
    };
    setExpenses([...expenses, newExpense]);
    handleCloseAddMenu();
  };

  const handleRemoveExpense = (index: number) => {
    const expense = expenses[index];
    // Don't allow removing "Otros gastos"
    if (expense.name === 'Otros gastos') return;

    const updatedExpenses = expenses.filter((_, i) => i !== index);
    setExpenses(updatedExpenses);
  };

  const handleExpenseChange = (
    index: number,
    field: 'name' | 'amount' | 'hasIva',
    value: string | number | boolean,
  ) => {
    const updatedExpenses = [...expenses];

    if (field === 'amount') {
      updatedExpenses[index] = {
        ...updatedExpenses[index],
        amount:
          typeof value === 'string'
            ? formatMoneyBack(value)
            : (value as number),
      };
    } else if (field === 'hasIva') {
      updatedExpenses[index] = {
        ...updatedExpenses[index],
        hasIva: Boolean(value),
      };
    } else {
      updatedExpenses[index] = {
        ...updatedExpenses[index],
        name: String(value),
      };
    }

    // Merge expenses with same name
    const mergedExpenses = mergeExpenses(updatedExpenses);
    setExpenses(mergedExpenses);
  };

  const mergeExpenses = (
    expensesList: { name: string; amount: number; hasIva: boolean }[],
  ) => {
    const mergedMap = new Map<
      string,
      { name: string; amount: number; hasIva: boolean }
    >();

    expensesList.forEach((exp) => {
      if (exp.name === '') {
        mergedMap.set(exp.name + Math.random(), exp);
      } else {
        const existing = mergedMap.get(exp.name);
        if (existing) {
          mergedMap.set(exp.name, {
            name: exp.name,
            amount: existing.amount + exp.amount,
            hasIva: existing.hasIva,
          });
        } else {
          mergedMap.set(exp.name, exp);
        }
      }
    });

    return Array.from(mergedMap.values());
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <TextField
          value={formatMoney(totalAmount)}
          type="text"
          disabled
          fullWidth
        />
        <IconButton
          onClick={handleTogglePopper}
          size="small"
          disabled={!onEditing}
        >
          <Add />
        </IconButton>
      </div>

      <Popper
        open={openPopper}
        placement="bottom-start"
        style={{
          zIndex: 1300,
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '50%',
        }}
      >
        <Paper
          elevation={3}
          style={{
            padding: 16,
            minWidth: 600,
            maxHeight: 400,
            overflow: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Gastos de la Operación
            </Typography>
          </div>

          {expenses.map((expense, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
              }}
            >
              <TextField
                value={expense.name}
                onChange={(e) =>
                  handleExpenseChange(index, 'name', e.target.value)
                }
                disabled={!requestConfiguration.canCreateRequestExpenses}
                placeholder="Nombre del gasto"
                size="small"
                style={{ flex: 2 }}
                inputProps={{ maxLength: 189 }}
              />
              <TextField
                value={formatMoney(expense.amount)}
                onChange={(e) =>
                  handleExpenseChange(index, 'amount', e.target.value)
                }
                placeholder="Monto"
                type="text"
                size="small"
                style={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={expense.hasIva}
                    onChange={(e) =>
                      handleExpenseChange(index, 'hasIva', e.target.checked)
                    }
                    size="small"
                    disabled={!requestConfiguration.expensesHasIVA}
                  />
                }
                label="IVA"
                style={{ margin: 0 }}
              />
              {expense.name !== 'Otros gastos' && (
                <IconButton
                  onClick={() => handleRemoveExpense(index)}
                  size="small"
                  style={{ color: '#D62839' }}
                >
                  <Delete />
                </IconButton>
              )}
            </div>
          ))}

          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <IconButton
                  onClick={handleOpenAddMenu}
                  size="small"
                  style={{
                    color: '#0F23DF',
                    border: '1px solid #0F23DF',
                    borderRadius: 4,
                  }}
                >
                  <Add />
                </IconButton>
                <Typography variant="body2" style={{ alignSelf: 'center' }}>
                  Agregar gasto
                </Typography>
              </div>
              <CustomButton color="primary" onClick={handleTogglePopper}>
                Confirmar
              </CustomButton>
            </div>
          </div>

          <Menu
            open={openMenu}
            anchorEl={menuAnchorEl}
            onClose={handleCloseAddMenu}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: 300,
              },
            }}
          >
            {requestConfiguration.canCreateRequestExpenses && (
              <MenuItem onClick={handleAddBlankExpense}>
                <Typography variant="body2">Gasto en blanco</Typography>
              </MenuItem>
            )}
            {requestExpensesCategories.map((category) => (
              <MenuItem
                key={category.id}
                onClick={() => handleAddCategoryExpense(category)}
              >
                <Typography variant="body2">{category.name}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </Paper>
      </Popper>
    </>
  );
};

export default RequestExpenses;
